import React from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from 'date-fns';
import { FeedbackDetailsSheet } from "@/components/feedback/FeedbackDetailsSheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type FeedbackStatus = 'unread' | 'reviewed' | 'in_process' | 'roadmap' | 'resolved';

interface Feedback {
  id: string;
  feedback: string;
  created_at: string;
  can_contact: boolean;
  status: FeedbackStatus;
  email: string | null;
  rating: string;
  user_id: string;
  updated_at: string;
}

const FeedbackTable: React.FC = () => {
  const [selectedFeedback, setSelectedFeedback] = React.useState<Feedback | null>(null);
  const [feedbackToDelete, setFeedbackToDelete] = React.useState<string | null>(null);

  const { data: feedback = [], refetch, isLoading } = useQuery({
    queryKey: ['all-feedback'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_all_feedback');
      if (error) throw error;
      return data as Feedback[];
    }
  });

  const handleStatusUpdate = async (id: string, status: FeedbackStatus, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from('user_feedback')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
      toast.success('Feedback status updated');
      refetch();
    } catch (error) {
      console.error('Error updating feedback status:', error);
      toast.error('Failed to update feedback status');
    }
  };

  const handleDelete = async () => {
    if (!feedbackToDelete) return;
    try {
      const { error } = await supabase
        .from('user_feedback')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', feedbackToDelete);
      if (error) throw error;
      setSelectedFeedback(selectedFeedback?.id === feedbackToDelete ? null : selectedFeedback);
      setFeedbackToDelete(null);
      toast.success('Feedback deleted successfully');
      await refetch();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast.error('Failed to delete feedback');
      setFeedbackToDelete(null);
    }
  };

  const handleDeleteClick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFeedbackToDelete(id);
  };

  const getEmojiForRating = (rating: string) => {
    switch (rating) {
      case 'very_negative': return '😖';
      case 'negative': return '😡';
      case 'neutral': return '😐';
      case 'positive': return '🙂';
      case 'very_positive': return '😍';
      default: return '❓';
    }
  };

  const handleRowClick = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
  };

  const handleSheetClose = () => {
    setSelectedFeedback(null);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4">All User Feedback</h2>
      {isLoading ? (
        <div className="py-8 text-center">Loading feedback data...</div>
      ) : feedback.length === 0 ? (
        <div className="py-8 text-center text-[#5A636E]">
          No feedback found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Feedback</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead className="hidden sm:table-cell">Email</TableHead>
                <TableHead className="hidden sm:table-cell">Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feedback.map((item: Feedback) => (
                <TableRow 
                  key={item.id} 
                  className="cursor-pointer hover:bg-[#E8F0F8]"
                  onClick={() => handleRowClick(item)}
                >
                  <TableCell className="whitespace-nowrap">
                    {format(new Date(item.created_at), 'dd/MM/yy HH:mm')}
                  </TableCell>
                  <TableCell className="max-w-[150px] sm:max-w-[200px]">
                    <div className="truncate">
                      {item.feedback.length > 60 
                        ? `${item.feedback.substring(0, 60)}...` 
                        : item.feedback}
                    </div>
                  </TableCell>
                  <TableCell>{getEmojiForRating(item.rating)}</TableCell>
                  <TableCell className="hidden sm:table-cell">{item.email || 'N/A'}</TableCell>
                  <TableCell className="hidden sm:table-cell">{item.can_contact ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="whitespace-nowrap">{item.status}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Button 
                        size="sm" 
                        variant={item.status === 'reviewed' ? 'default' : 'outline'}
                        onClick={(e) => handleStatusUpdate(item.id, 'reviewed', e)}
                      >
                        Reviewed
                      </Button>
                      <Button 
                        size="sm"
                        variant={item.status === 'in_process' ? 'default' : 'outline'}
                        onClick={(e) => handleStatusUpdate(item.id, 'in_process', e)}
                      >
                        In Process
                      </Button>
                      <Button 
                        size="sm"
                        variant={item.status === 'resolved' ? 'default' : 'outline'}
                        onClick={(e) => handleStatusUpdate(item.id, 'resolved', e)}
                      >
                        Resolved
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleDeleteClick(item.id, e)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {selectedFeedback && (
        <FeedbackDetailsSheet
          isOpen={!!selectedFeedback}
          onClose={handleSheetClose}
          feedback={selectedFeedback}
        />
      )}

      <AlertDialog open={!!feedbackToDelete} onOpenChange={(open) => !open && setFeedbackToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Feedback</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this feedback? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeedbackTable; 